import React from 'react';
import { Helmet } from 'react-helmet';

function ColorSwatch({ name, variable }) {
  return (
    <div className="panel" style={{ padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ display: 'inline-block', width: 28, height: 28, borderRadius: 6, background: `var(${variable})`, border: '1px solid var(--color-border)' }} />
        <div>
          <strong>{name}</strong>
          <div><code>{variable}</code></div>
        </div>
      </div>
    </div>
  );
}

function DemoPage() {
  return (
    <main className="container" style={{ paddingTop: 32, paddingBottom: 32 }}>
      <Helmet>
        <title>Design System Demo — The Big Idea</title>
        <meta name="robots" content="noindex,nofollow" />
        <link rel="canonical" href={(process.env.REACT_APP_SITE_URL || 'https://bigidea.muware.xyz') + '/demo'} />
      </Helmet>
      <h1>Design System Demo</h1>
      <p className="text-muted">Overview of base elements styled by the app.</p>

      <section className="panel panel--elevated" style={{ marginTop: 24 }}>
        <h2>Typography</h2>
        <h1>Heading 1</h1>
        <h2>Heading 2</h2>
        <h3>Heading 3</h3>
        <h4>Heading 4</h4>
        <h5>Heading 5</h5>
        <h6>Heading 6</h6>
        <p>
          Regular paragraph with <strong>bold text</strong>, <em>emphasis</em>, and a
          <a href="#" style={{ marginLeft: 6 }}> link</a>.
        </p>
        <small>Small helper text</small>
        <hr />
        <blockquote>
          This is a blockquote. Use it to call out key information.
        </blockquote>
        <pre><code>{`function greet(name) {
  return 'Hello ' + name;
}`}</code></pre>
        <div className="grid gap-2" style={{ marginTop: 16 }}>
          <div>
            <h4>Unordered list</h4>
            <ul>
              <li>First item</li>
              <li>Second item</li>
              <li>Third item</li>
            </ul>
          </div>
          <div>
            <h4>Ordered list</h4>
            <ol>
              <li>Step one</li>
              <li>Step two</li>
              <li>Step three</li>
            </ol>
          </div>
        </div>
      </section>

      <section className="panel" style={{ marginTop: 24 }}>
        <h2>Buttons</h2>
        <div className="stack">
          <div>
            <button className="btn btn--primary">Primary</button>{' '}
            <button className="btn btn--secondary">Secondary</button>{' '}
            <button className="btn btn--outline">Outline</button>{' '}
            <button className="btn btn--soft">Soft</button>{' '}
            <button className="btn btn--success">Success</button>{' '}
            <button className="btn btn--danger">Danger</button>
          </div>
          <div>
            <button className="btn btn--primary btn--sm">Small</button>{' '}
            <button className="btn btn--primary">Medium</button>{' '}
            <button className="btn btn--primary btn--lg">Large</button>{' '}
            <button className="btn btn--secondary btn-icon" aria-label="star">★</button>
          </div>
          <div className="btn-group">
            <button className="btn btn--outline">Left</button>
            <button className="btn btn--outline">Middle</button>
            <button className="btn btn--outline">Right</button>
          </div>
          <div>
            <button className="btn btn--primary" disabled>Disabled</button>
          </div>
        </div>
      </section>

      <section className="panel" style={{ marginTop: 24 }}>
        <h2>Forms</h2>
        <form className="stack" onSubmit={(e) => e.preventDefault()}>
          <div className="form-row cols-2">
            <div>
              <label className="form-label" htmlFor="name">Name</label>
              <input id="name" className="field" placeholder="Jane Doe" />
            </div>
            <div>
              <label className="form-label" htmlFor="email">Email</label>
              <input id="email" type="email" className="field" placeholder="jane@acme.com" />
            </div>
          </div>
          <div className="form-row cols-3">
            <div>
              <label className="form-label" htmlFor="role">Role</label>
              <select id="role" className="field">
                <option>Developer</option>
                <option>Product</option>
                <option>Design</option>
              </select>
            </div>
            <div>
              <label className="form-label" htmlFor="qty">Quantity</label>
              <input id="qty" type="number" className="field field--sm" defaultValue={1} />
              <div className="form-help">Use a small input for compact forms.</div>
            </div>
            <div>
              <label className="form-label" htmlFor="price">Price</label>
              <div className="input-group">
                <span className="addon">$</span>
                <input id="price" type="number" className="field" defaultValue={99} />
                <span className="addon">.00</span>
              </div>
            </div>
          </div>
          <div>
            <label className="form-label" htmlFor="msg">Message</label>
            <textarea id="msg" className="field" placeholder="Write something..." />
          </div>
          <div className="grid gap-2" style={{ alignItems: 'center' }}>
            <label className="control">
              <input type="checkbox" defaultChecked />
              <span>Subscribe to newsletter</span>
            </label>
            <label className="control">
              <input type="radio" name="r" defaultChecked />
              <span>Option A</span>
            </label>
            <label className="control">
              <input type="radio" name="r" />
              <span>Option B</span>
            </label>
            <label className="control">
              <span>Enable notifications</span>
              <span className="switch">
                <input type="checkbox" defaultChecked />
                <span className="slider" />
              </span>
            </label>
          </div>
          <div>
            <button className="btn btn--primary" type="submit">Submit</button>{' '}
            <button className="btn btn--outline" type="button">Cancel</button>
          </div>
        </form>
      </section>

      <section className="panel" style={{ marginTop: 24 }}>
        <h2>Tables</h2>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Jane</td>
              <td>Developer</td>
              <td>Active</td>
            </tr>
            <tr>
              <td>John</td>
              <td>Designer</td>
              <td>Inactive</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="panel" style={{ marginTop: 24 }}>
        <h2>Panels & Grid</h2>
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="panel">Simple panel</div>
          <div className="panel panel--muted">Muted panel</div>
          <div className="panel panel--elevated">Elevated panel</div>
        </div>
      </section>

      <section className="panel" style={{ marginTop: 24 }}>
        <h2>Color Tokens</h2>
        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <ColorSwatch name="Primary" variable="--color-primary" />
          <ColorSwatch name="Secondary" variable="--color-secondary" />
          <ColorSwatch name="Success" variable="--color-success" />
          <ColorSwatch name="Warning" variable="--color-warning" />
          <ColorSwatch name="Danger" variable="--color-danger" />
          <ColorSwatch name="Border" variable="--color-border" />
        </div>
      </section>
    </main>
  );
}

export default DemoPage;


